import gdprConsent from './gdpr-consent.js';
import scheduling from './scheduling.js';

window.addEventListener("DOMContentLoaded", () => {
  // Initialize GDPR consent
  gdprConsent();

  // The scheduling block: its dialog, and what a booking is answered with
  scheduling();

  /* Add hljs class to all code blocks for highlight.js */
  document.querySelectorAll('pre code').forEach((block) => {
    block.classList.add('hljs');
  });

  /* Add IDs to article headers for anchor links */
  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Collapse multiple hyphens
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const usedIds = new Set();
  document.querySelectorAll('article h1, article h2, article h3, article h4, article h5, article h6').forEach((header) => {
    if (header.id) {
      usedIds.add(header.id);
      return;
    }

    let baseId = slugify(header.textContent);
    let id = baseId;
    let counter = 1;

    while (usedIds.has(id)) {
      id = `${baseId}-${counter}`;
      counter++;
    }

    usedIds.add(id);
    header.id = id;
  });

  /* Mobile Menu
   *
   * The sub-menu chevron is rendered by templates/_partials/navigation-mobile.php.
   * It used to be injected here as well as in theme.js, and because this ran first
   * on DOMContentLoaded while the deferred theme.js added its own unconditionally,
   * the Services item ended up with two stacked chevrons. */

  /* Header menu that gets out of its own way
   *
   * The desktop nav switches on at 941px but needs 1020px to lay out -- logo,
   * seven items, socials and search, none of them able to shrink -- so between
   * those two widths .actions hung past .wrap-head and the whole page scrolled
   * sideways by the difference. Rather than move the breakpoint and lose the
   * menu on a small laptop, the items marked `optional` in _navigation.toml
   * fold into a "More" dropdown until what is left fits.
   *
   * Measured against .actions rather than scrollWidth: .actions is the element
   * that overhangs, and reading its right edge does not care what else on the
   * page might be overflowing for reasons of its own.
   *
   * Against the content edge, not the border edge. .wrap-head is padded 35px --
   * the gutter the logo sits on at the other end -- and .actions reaches its
   * side of it with margin-left:auto. An auto margin has nothing left to give
   * once the line is full, so the row eats the padding before it overflows the
   * element: at 956px the search icon still sat inside .wrap-head and so read
   * as fitting, with 4px of the 35px gutter left and the icon against the
   * window. Folding while it is inside the padding means the gutter is the
   * safety margin, and it is the one the design already uses rather than a
   * number picked to look right.
   */
  const head = document.querySelector('.main-head .wrap-head');
  const menu = head && head.querySelector('#menu-main-menu');
  const more = menu && menu.querySelector('.menu-item-more');

  if (head && menu && more) {
    const actions = head.querySelector('.actions');
    const moreList = more.querySelector('.sub-menu');
    const optional = [...menu.querySelectorAll('[data-nav-optional]')];

    // How many items the dropdown has to be holding to be worth showing.
    const MIN_FOLD = 2;

    // Where each item goes back to. Captured before anything moves, because
    // once one is folded its neighbour's sibling is no longer what it was.
    const seats = optional.map((item) => [item, item.nextElementSibling]);

    const overflows = () => {
      const pad = parseFloat(getComputedStyle(head).paddingRight) || 0;

      return actions.getBoundingClientRect().right >
        head.getBoundingClientRect().right - pad + 1;
    };

    const fit = () => {
      // Put everything back first, so widening the window returns items and not
      // just stops taking them. Nothing paints mid-function, so this does not
      // flash.
      //
      // Last to first, because an optional item's seat can be another optional
      // item -- Careers sits before Initiatives -- and insertBefore throws if
      // the node it is given is not currently a child. Going backwards, a seat
      // is either a fixed item or one already put back. Forwards it threw on
      // the first widen and left the menu folded for the rest of the session.
      for (let i = seats.length - 1; i >= 0; i--) {
        menu.insertBefore(seats[i][0], seats[i][1]);
      }
      more.classList.remove('is-shown');
      moreList.replaceChildren();

      // From the end, because the last optional item is the one the menu can
      // most afford to lose. Showing .more first: it takes width of its own,
      // and the fold has to be worth more than the dropdown costs.
      let folded = 0;

      for (let i = optional.length - 1; i >= 0 && overflows(); i--) {
        more.classList.add('is-shown');
        moreList.insertBefore(optional[i], moreList.firstChild);
        folded++;
      }

      // A dropdown standing there to hide one item is not worth the width it
      // takes to say so -- roughly what the item took. Once folding has started
      // it takes a second, which for the two optional items there are now means
      // the dropdown is either empty or holds both.
      while (folded > 0 && folded < MIN_FOLD && folded < optional.length) {
        moreList.insertBefore(optional[optional.length - 1 - folded], moreList.firstChild);
        folded++;
      }

      if (!folded) {
        more.classList.remove('is-shown');
      }
    };

    fit();

    // The menu is set in a webfont, so its width before the font lands is not
    // the width it keeps.
    if (document.fonts) {
      document.fonts.ready.then(fit);
    }

    let queued = false;
    window.addEventListener('resize', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fit();
      });
    });
  }

  /* FAQ */
  document.querySelectorAll(".makimo-blocks-faq").forEach((faq) => {
    const items = faq.querySelectorAll("[data-faq-item]");

    items.forEach((item) => {
      const button = item.querySelector(".makimo-blocks-faq__question");
      const answer = item.querySelector(".makimo-blocks-faq__answer");
      if (!button || !answer) return;

      button.addEventListener("click", () => {
        const willOpen = !item.classList.contains("is-open");

        items.forEach((other) => {
          const otherButton = other.querySelector(".makimo-blocks-faq__question");
          const otherAnswer = other.querySelector(".makimo-blocks-faq__answer");
          other.classList.remove("is-open");
          if (otherButton) otherButton.setAttribute("aria-expanded", "false");
          if (otherAnswer) otherAnswer.hidden = true;
        });

        if (willOpen) {
          item.classList.add("is-open");
          button.setAttribute("aria-expanded", "true");
          answer.hidden = false;
        }
      });
    });
  });

  /* Snap scroller
   *
   * The markup contract lives in src/styles/_snap-scroller.scss. Four blocks
   * and the blog mega menu use it.
   */
  document.querySelectorAll(".snap-scroller").forEach((item) => {
    const container = item.querySelector(".snap-container");
    const controls = item.querySelector(".snap-controls");

    if (!container || !controls) return;

    const leftArrow = controls.querySelector(".left");
    const rightArrow = controls.querySelector(".right");
    const progressBar = controls.querySelector(".progress");

    if (!leftArrow || !rightArrow) return;

    /* Two opt-in behaviours, both for the mega menu, both matching what its
     * slick config used to do:
     *
     *   data-snap-page   advance a whole viewport rather than one item, the
     *                    equivalent of slick's slidesToScroll -- five posts
     *                    fill that strip exactly, so a page is five posts.
     *   data-snap-loop   wrap at the ends instead of stopping, so the strip
     *                    still cycles [1-5] -> [6-10] -> [1-5] the way
     *                    slick's `infinite` did.
     *
     * Without them the arrows step one item and stop at each end, which is
     * what the four block carousels have always done. */
    const paged = item.hasAttribute("data-snap-page");
    const loops = item.hasAttribute("data-snap-loop");

    const targetFor = (direction) => {
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (paged) {
        const target = container.scrollLeft + direction * container.clientWidth;

        // Past either end: wrap when asked to, otherwise rest against it. The
        // 2px slack absorbs the sub-pixel widths a percentage-based track
        // produces, which would otherwise leave a page unreachable.
        if (target > maxScroll + 2) return loops ? 0 : maxScroll;
        if (target < -2) return loops ? maxScroll : 0;

        return Math.min(maxScroll, Math.max(0, target));
      }

      const containerLeft = container.getBoundingClientRect().left;
      const children = [...container.children];
      const step = direction > 0
        ? children.find((c) => c.getBoundingClientRect().left > containerLeft + 2)
        : children.reverse().find((c) => c.getBoundingClientRect().left < containerLeft - 2);

      return step
        ? container.scrollLeft + step.getBoundingClientRect().left - containerLeft
        : container.scrollLeft;
    };

    /* preventDefault because a control may be an anchor: the mega menu's
     * arrows are <a href="#">, and without this a click also jumps the page to
     * the top. The blocks' arrows are bare <svg> and have no default action. */
    const bindArrow = (arrow, direction) => {
      arrow.addEventListener("click", (e) => {
        e.preventDefault();
        if (arrow.classList.contains("inactive")) return;
        container.scrollTo(targetFor(direction), 0);
      });
    };

    bindArrow(leftArrow, -1);
    bindArrow(rightArrow, 1);

    const updateScrollStatus = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;

      controls.classList.toggle("visible", maxScroll > 0);

      // A looping strip has no dead ends, so neither arrow ever greys out.
      leftArrow.classList.toggle("inactive", !loops && container.scrollLeft <= 2);
      rightArrow.classList.toggle("inactive", !loops && container.scrollLeft >= maxScroll - 2);

      if (progressBar) {
        // maxScroll is 0 for a strip that fits, and for one that has not been
        // laid out yet; either way the division is not a number worth writing.
        const pct = maxScroll > 0
          ? Math.min(100, Math.max(0, Math.round((container.scrollLeft / maxScroll) * 100)))
          : 0;
        progressBar.style.setProperty("--percentage", `${pct}%`);
      }
    };

    container.addEventListener("scroll", updateScrollStatus);

    /* Set the initial state synchronously. The observer below also fires once
     * on observe(), but its callbacks are delivered before paint -- so in a tab
     * that is not painting, they are not delivered at all, and the controls
     * would stay hidden until the tab came forward. */
    updateScrollStatus();

    /* A ResizeObserver rather than a window resize listener, because the strip
     * can change size without the window doing so. The mega menu is the case
     * that forced it: below 940px its whole dropdown is display:none, so the
     * container measures zero, and a resize up to desktop has to re-measure.
     * Observing the container catches that, and any reflow that changes how
     * much of the strip fits. */
    new ResizeObserver(updateScrollStatus).observe(container);
  });

  /* Search Modal - the only place it is set up. Contentberg's theme.js carried an
   * identical magnificPopup call against the same link and, running on jQuery ready,
   * won the race; this copy then no-opped on its own already-initialised guard. */
  function initSearchModal() {
    if (typeof jQuery === 'undefined' || !jQuery.fn.magnificPopup) {
      return false;
    }
    
    const searchLink = document.querySelector('.main-head.has-search-modal .search-link');
    if (!searchLink) {
      return false;
    }

    // Check if already initialized
    if (jQuery(searchLink).data('magnificPopup')) {
      return true;
    }

    jQuery(searchLink).magnificPopup({
      items: {
        src: '.search-modal-wrap',
        type: 'inline'
      },
      removalDelay: 400,
      focus: 'input',
      closeBtnInside: false,
      closeOnBgClick: false,
      mainClass: 'search-modal',
      midClick: true,
      fixedContentPos: true,
      autoFocusLast: false
    });
    return true;
  }

  // Every script on the page is deferred, so they have all run by the time this
  // handler fires -- magnific-popup.js included. Bind now rather than waiting for
  // window load, which used to leave the link inert for up to half a second after
  // the page was interactive. theme.js bound the same modal first and masked that;
  // it no longer does, so the retries below are the only safety net left. They are
  // harmless either way: initSearchModal() returns early if it has already run.
  if (!initSearchModal()) {
    window.addEventListener('load', initSearchModal);
    setTimeout(initSearchModal, 500);
  }

  /* Infinite Scroll for Blog */
  (function() {
    const trigger = document.getElementById('blog-infinite-scroll-trigger');
    if (!trigger) return;

    const container = document.getElementById('blog-posts-container');
    if (!container) return;

    const spinner = trigger.querySelector('.spinner');
    let isLoading = false;
    let nextOffset = parseInt(trigger.dataset.nextOffset) || 0;
    const postsPerPage = parseInt(trigger.dataset.postsPerPage) || 6;
    const ajaxUrl = trigger.dataset.ajaxUrl || '/blog/';

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isLoading) {
        loadMore();
      }
    }, { rootMargin: '200px' });

    observer.observe(trigger);

    async function loadMore() {
      isLoading = true;
      if (spinner) spinner.style.display = 'inline';

      try {
        // Handle URLs that already have query parameters (e.g., search)
        const separator = ajaxUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${ajaxUrl}${separator}offset=${nextOffset}&limit=${postsPerPage}`);
        const html = await response.text();

        const temp = document.createElement('div');
        temp.innerHTML = html;

        // Check for completion marker
        if (temp.querySelector('[data-blog-complete]')) {
          observer.disconnect();
          trigger.remove();
          return;
        }

        // Append posts with animation
        temp.querySelectorAll('.col-4').forEach(post => {
          post.classList.add('fade-in-up-lg');
          container.appendChild(post);
        });

        nextOffset += postsPerPage;
        trigger.dataset.nextOffset = nextOffset;

      } catch (e) {
        console.error('Infinite scroll error:', e);
      }

      if (spinner) spinner.style.display = 'none';
      isLoading = false;
    }
  })();

  /* Competencies Catalogue */
  (function() {
    const dataEl = document.getElementById('competencies-data');
    if (!dataEl) return;

    const searchInput = document.querySelector('.competencies-search');
    const categoryButtons = document.querySelectorAll('[data-filter-category]');
    const areaButtons = document.querySelectorAll('[data-filter-area]');
    const sections = document.querySelectorAll('.competency-area');

    let activeCategory = 'all';
    let activeArea = null;

    function applyFilters() {
      const searchTerm = (searchInput?.value || '').toLowerCase().trim();

      sections.forEach((section) => {
        const category = section.getAttribute('data-category');
        const area = section.getAttribute('data-area');
        const text = section.textContent.toLowerCase();

        const matchesCategory = activeCategory === 'all' || category === activeCategory;
        const matchesArea = !activeArea || area === activeArea;
        const matchesSearch = !searchTerm || text.includes(searchTerm);

        section.classList.toggle('hidden', !(matchesCategory && matchesArea && matchesSearch));
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', applyFilters);
    }

    categoryButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        activeCategory = btn.getAttribute('data-filter-category');
        activeArea = null;

        categoryButtons.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        areaButtons.forEach((b) => b.classList.remove('active'));

        // Show/hide area pills based on selected category
        areaButtons.forEach((b) => {
          const parentCat = b.getAttribute('data-filter-parent-category');
          b.style.display = (activeCategory === 'all' || parentCat === activeCategory) ? '' : 'none';
        });

        applyFilters();
      });
    });

    areaButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const area = btn.getAttribute('data-filter-area');

        if (activeArea === area) {
          activeArea = null;
          btn.classList.remove('active');
        } else {
          activeArea = area;
          areaButtons.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
        }

        applyFilters();
      });
    });
  })();

  /* Position share content relative to post-content */
  function positionShareFloat() {
    const shareFloat = document.querySelector('.post-share-float');
    if (!shareFloat) return;

    const postContent = document.querySelector('.post-content.entry-content');
    if (!postContent) return;

    const article = shareFloat.closest('article');
    if (!article) return;

    // Get the position of post-content relative to the article
    const articleTop = article.getBoundingClientRect().top + window.scrollY;
    const contentTop = postContent.getBoundingClientRect().top + window.scrollY;
    const offset = contentTop - articleTop;
    
    // Set the top position of the share float to align with post-content start
    shareFloat.style.top = `${offset}px`;
    
    // Set height to match post-content height so sticky works correctly
    shareFloat.style.height = `${postContent.offsetHeight}px`;
  }

  // Position on load and resize
  if (document.querySelector('.post-share-float')) {
    // Run after a short delay to ensure layout is complete
    setTimeout(positionShareFloat, 100);
    window.addEventListener('resize', positionShareFloat);
  }

});
